import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { Transition } from 'react-transition-group';
const TRANSITION_DURATION = 300;
export const Collapse = forwardRef(({
  in: inProp,
  component: Component = 'div',
  className,
  children,
  onEntering,
  onEntered,
  minHeight,
  onEnter,
  onExit,
  onExited,
  onExiting,
  ...restProps
}, ref) => {
  const componentRef = useRef(null);
  useImperativeHandle(ref, () => ({
    flush: () => {
      if (componentRef.current && componentRef.current.style.height !== '0px') {
        componentRef.current.style.height = 'auto';
        componentRef.current.style.height = `${componentRef.current.scrollHeight}px`;
      }
    }
  }));
  const handleEntering = useCallback(isAppearing => {
    if (!componentRef.current) {
      return;
    }
    const height = componentRef.current.scrollHeight;
    componentRef.current.style.height = `${height}px`;
    if (typeof onEntering === 'function') {
      onEntering(componentRef.current, isAppearing);
    }
  }, [onEntering]);
  const handleEntered = useCallback(isAppearing => {
    if (!componentRef.current) {
      return;
    }
    const height = componentRef.current.scrollHeight;
    componentRef.current.style.height = `${height}px`;
    if (typeof onEntered === 'function') {
      onEntered(componentRef.current, isAppearing);
    }
  }, [onEntered]);
  const transitionStyles = useMemo(() => ({
    default: {
      transition: `all ${TRANSITION_DURATION}ms ease-in-out`,
      minHeight: minHeight ? `${minHeight}px` : undefined,
      height: 0,
      overflow: 'hidden'
    },
    transitions: {
      entered: {
        height: 'auto',
        overflow: 'visible'
      },
      exiting: {
        visibility: minHeight ? 'visible' : 'hidden'
      },
      exited: {
        visibility: minHeight ? 'visible' : 'hidden'
      }
    }
  }), [minHeight]);
  const handleOnEnter = useCallback(isAppearing => {
    if (componentRef.current) {
      onEnter?.(componentRef.current, isAppearing);
    }
  }, [onEnter]);
  const handleOnExit = useCallback(() => {
    if (componentRef.current) {
      onExit?.(componentRef.current);
    }
  }, [onExit]);
  const handleOnExited = useCallback(() => {
    if (componentRef.current) {
      onExited?.(componentRef.current);
    }
  }, [onExited]);
  const handleOnExiting = useCallback(() => {
    if (componentRef.current) {
      onExiting?.(componentRef.current);
    }
  }, [onExiting]);
  return _jsx(Transition, {
    ...restProps,
    in: inProp,
    timeout: TRANSITION_DURATION,
    onEntering: handleEntering,
    onEntered: handleEntered,
    nodeRef: componentRef,
    onEnter: handleOnEnter,
    onExit: handleOnExit,
    onExited: handleOnExited,
    onExiting: handleOnExiting,
    children: state => _jsx(Component, {
      ref: componentRef,
      className: className,
      style: {
        ...transitionStyles.default,
        ...transitionStyles.transitions[state]
      },
      "aria-hidden": !inProp,
      children: children
    })
  });
});